import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { LoadingState } from '../../utilities/constants';
import { toast } from 'react-toastify';
import { uploadBillToS3Api, parseBillApi, saveBillApi, fetchBillsListApi, downloadBillApi } from './billParsingApi';

export const uploadAndParseBill = createAsyncThunk('billParsing/uploadAndParse', async (payload) => {
  try {
    const { orgId, file } = payload;
    const { key, name } = await uploadBillToS3Api(orgId, file);
    const parseResponse = await parseBillApi(orgId, { key, name });
    return parseResponse;
  } catch (error) {
    toast.error(error.message);
    throw error;
  }
});

export const saveBill = createAsyncThunk('billParsing/save', async (payload) => {
  try {
    const { orgId, data } = payload;
    const result = await saveBillApi(orgId, data);
    if (result?.results) {
      toast.success(`Successfully saved ${result.results.savedMeters} out of ${result.results.totalMeters} meters`);
    }
    return result;
  } catch (error) {
    toast.error(error.message);
    throw error;
  }
});

export const fetchBillsList = createAsyncThunk('billParsing/fetchList', async (payload) => {
  try {
    const { orgId, page, limit } = payload;
    const response = await fetchBillsListApi(orgId, page, limit);
    return response;
  } catch (error) {
    toast.error(error.message);
    throw error;
  }
});

export const downloadBill = createAsyncThunk('billParsing/download', async (payload) => {
  try {
    const { orgId, fileId } = payload;
    const response = await downloadBillApi(orgId, fileId);
    window.open(response.downloadUrl, '_blank');
    return response;
  } catch (error) {
    toast.error(error.message);
    throw error;
  }
});

const billParsingSlice = createSlice({
  name: 'billParsing',
  initialState: {
    uploadStatus: LoadingState.idle,
    parseStatus: LoadingState.idle,
    saveStatus: LoadingState.idle,
    listStatus: LoadingState.idle,
    parsedData: null,
    saveResult: null,
    bills: [],
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0
  },
  reducers: {
    clearParsedData: (state) => {
      state.parsedData = null;
      state.uploadStatus = LoadingState.idle;
      state.parseStatus = LoadingState.idle;
    },
    clearSaveResult: (state) => {
      state.saveResult = null;
      state.saveStatus = LoadingState.idle;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(uploadAndParseBill.pending, (state) => {
        state.uploadStatus = LoadingState.pending;
        state.parseStatus = LoadingState.pending;
      })
      .addCase(uploadAndParseBill.fulfilled, (state, action) => {
        state.uploadStatus = LoadingState.succeeded;
        state.parseStatus = LoadingState.succeeded;
        state.parsedData = action.payload;
      })
      .addCase(uploadAndParseBill.rejected, (state) => {
        state.uploadStatus = LoadingState.failed;
        state.parseStatus = LoadingState.failed;
      })
      .addCase(saveBill.pending, (state) => {
        state.saveStatus = LoadingState.pending;
      })
      .addCase(saveBill.fulfilled, (state, action) => {
        state.saveStatus = LoadingState.succeeded;
        state.saveResult = action.payload;
      })
      .addCase(saveBill.rejected, (state) => {
        state.saveStatus = LoadingState.failed;
      })
      .addCase(fetchBillsList.pending, (state) => {
        state.listStatus = LoadingState.pending;
      })
      .addCase(fetchBillsList.fulfilled, (state, action) => {
        state.listStatus = LoadingState.succeeded;
        state.bills = Array.isArray(action.payload) ? action.payload : [];
        state.total = state.bills.length;
        state.totalPages = Math.ceil(state.total / state.limit);
      })
      .addCase(fetchBillsList.rejected, (state) => {
        state.listStatus = LoadingState.failed;
      });
  }
});

export const { clearParsedData, clearSaveResult } = billParsingSlice.actions;
export default billParsingSlice.reducer;
